import { useState } from 'react';
import RichTextEditor from 'react-rte';
import { RichUtils, Modifier, EditorState } from 'draft-js';
import { useDispatch } from 'react-redux';
import { Avatar, Grid, TextField, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Color } from '../../../../assets/color';
import RouteHeader from '../../../../components/common/RouteHeader';
import DatatableHeading from '../../../../components/heading/DatatableHeading';
import * as SuperadminActions from '../../../../redux/actions/superadminAction';
import { api_urls } from '../../../../utils/api-urls';
import { CrossSvg, UploadImageSvg } from '../../../../assets/svg';

//* Blog content: extra formatting options (font family + text color) on top
//* of react-rte's default Bold/Italic/Underline/Strikethrough toolbar.
const FONT_OPTIONS = [
    { label: 'Default', style: null },
    { label: 'Arial', style: 'FONT_ARIAL', css: { fontFamily: 'Arial, Helvetica, sans-serif' } },
    { label: 'Georgia', style: 'FONT_GEORGIA', css: { fontFamily: 'Georgia, serif' } },
    { label: 'Times New Roman', style: 'FONT_TIMES', css: { fontFamily: "'Times New Roman', Times, serif" } },
    { label: 'Courier New', style: 'FONT_COURIER', css: { fontFamily: "'Courier New', Courier, monospace" } },
    { label: 'Verdana', style: 'FONT_VERDANA', css: { fontFamily: 'Verdana, Geneva, sans-serif' } },
];

const COLOR_OPTIONS = [
    { label: 'Default', style: null },
    { label: 'Black', style: 'COLOR_BLACK', css: { color: '#000000' } },
    { label: 'Red', style: 'COLOR_RED', css: { color: '#e53e3e' } },
    { label: 'Blue', style: 'COLOR_BLUE', css: { color: '#3182ce' } },
    { label: 'Green', style: 'COLOR_GREEN', css: { color: '#38a169' } },
    { label: 'Orange', style: 'COLOR_ORANGE', css: { color: '#dd6b20' } },
    { label: 'Purple', style: 'COLOR_PURPLE', css: { color: '#805ad5' } },
];

//* customStyleMap: how the editor itself renders these styles while typing.
//* inlineStyles: how react-rte's HTML export (draft-js-export-html) turns
//* the same styles into real <span style="..."> markup that survives to
//* the saved blog content and therefore to the public site's rendering.
const blogCustomStyleMap = {};
const blogInlineStylesExportMap = {};
[...FONT_OPTIONS, ...COLOR_OPTIONS].forEach(opt => {
    if (opt.style) {
        blogCustomStyleMap[opt.style] = opt.css;
        blogInlineStylesExportMap[opt.style] = { element: 'span', style: opt.css };
    }
});

const AddBlogs = ({ mode }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const stateData = location?.state?.stateData;

    const [inputFieldDetail, setInputFieldDetail] = useState({ title: stateData?.title || '', content: stateData ? RichTextEditor.createValueFromString(String(stateData?.content), 'html') : RichTextEditor.createEmptyValue(), tags: stateData?.tags?.join(',') || '', author: stateData?.author || '' });
    const [inputFieldError, setInputFieldError] = useState({});

    const handleInputFieldDetail = (event) => setInputFieldDetail({ ...inputFieldDetail, [event?.target?.name]: event?.target?.value });  //* Handle Input Field : Data
    const handleInputFieldDetailError = (input, value) => setInputFieldError((prev) => ({ ...prev, [input]: value })); //* Handle Input Field : Error

    //! Apply a font/color inline style to the selected blog-content text.
    //* Removes any other style from the same group first - font and color
    //* are each mutually exclusive, selecting a new one should replace the
    //* old one rather than stack on top of it.
    const applyGroupStyle = (groupOptions, newStyle) => {
        const editorState = inputFieldDetail?.content?.getEditorState();
        if (!editorState) return;
        const selection = editorState.getSelection();
        if (selection.isCollapsed()) return;
        let contentState = editorState.getCurrentContent();
        groupOptions.forEach(opt => {
            if (opt.style) contentState = Modifier.removeInlineStyle(contentState, selection, opt.style);
        });
        let newEditorState = EditorState.push(editorState, contentState, 'change-inline-style');
        if (newStyle) newEditorState = RichUtils.toggleInlineStyle(newEditorState, newStyle);
        handleInputFieldDetail({ target: { name: 'content', value: inputFieldDetail.content.setEditorState(newEditorState) } });
    };

    const [image, setImage] = useState({ file: stateData ? api_urls + stateData?.cover_image : '', bytes: '' });
    const [bulkImage, setBulkImage] = useState(stateData ? stateData?.images?.map(value => { return { file: api_urls + value, bytes: '' } }) : []); //* Mutliple File 

    //! Handle Image : Normally
    const handleImage = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setImage({
                file: URL.createObjectURL(e.target.files[0]),
                bytes: e.target.files[0],
            });
        }

        handleInputFieldDetailError("image", null);
    };

    //! Handle Image : Drop Feature
    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setImage({
                file: URL.createObjectURL(e.dataTransfer.files[0]),
                bytes: e.dataTransfer.files[0],
            });
        }

        handleInputFieldDetailError("image", null);
    };

    //! Handle Image :  //* Bulk Image
    const handleBulkImage = (e) => {
        // console.log("Bulk Image length :: ", bulkImage?.length + 1)
        // if (bulkImage?.length + 1 <= 5) {
        if (e.target.files && e.target.files.length > 0) {
            setBulkImage([...bulkImage, {
                file: URL.createObjectURL(e.target.files[0]),
                bytes: e.target.files[0],
            }]);
        }
        // } else {
        //     alert('You have cross your limit bugger')
        // }
    };

    //! Handle Submit : Create | Edit
    const handleSubmit = () => {
        console.log({ ...inputFieldDetail, content: inputFieldDetail?.content?.toString('html'), image, bulkImage, tags: inputFieldDetail?.tags?.split(',')?.map(tag => tag.trim())?.filter(tag => tag) });
        const { title, content, tags, author } = inputFieldDetail;

        let formData = new FormData();

        formData.append('title', title);
        formData.append('content', content?.toString('html', { inlineStyles: blogInlineStylesExportMap }));
        formData.append('tags', inputFieldDetail?.tags?.split(',')?.map(tag => tag.trim())?.filter(tag => tag));
        formData.append('author', author);
        formData.append("cover_image", image?.bytes);
        bulkImage?.map(value => formData.append(`images`, value?.bytes));

        if (mode == 'Edit') {
            const payload = {
                id: stateData?._id,
                data: formData,
                onComplete: () => navigate(-1)
            }

            //* Dispatching API For Update
            dispatch(SuperadminActions.updateBlog(payload));

        } else {
            const payload = {
                data: formData,
                onComplete: () => navigate(-1)
            }

            //!*Dispatching API For Create
            dispatch(SuperadminActions.createBlog(payload));
        }
    }

    return (
        <>
            <RouteHeader title={`Blogs`} sub_title={`Manage blogs for your platform`} />

            <div style={{ backgroundColor: Color.white, padding: "20px", borderRadius: "10px" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <DatatableHeading title={'Blog Editor'} sub_title={'Create and publish blogs post'} />
                </div>

                <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} sm={12} md={12} xs={12} >
                        <div style={{ color: "#000", border: "1px solid #C4C4C4", borderRadius: "3px" }}>
                            {image?.file ?
                                <label onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} htmlFor="upload-image" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px", cursor: "pointer" }}>
                                    <div style={{ height: '300px' }}>
                                        <Avatar src={image.file} style={{ height: '100%', width: '100%', borderRadius: "initial", objectFit: 'contain' }} />
                                    </div>
                                </label>
                                :
                                <label onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} htmlFor="upload-image" style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center", padding: "100px 0", cursor: "pointer" }}>
                                    <UploadImageSvg h="80" w="80" color="#C4C4C4" />
                                    <div style={{ fontWeight: "600", fontSize: "18px" }}>Choose Your Image to Upload <span style={{ color: "red" }}>*</span></div>
                                    <div style={{ fontWeight: "500", fontSize: "16px", color: 'grey' }}>Or Drop Your Image Here</div>
                                </label>}
                            <input id="upload-image" onChange={handleImage} hidden accept="image/*" type="file" />
                        </div>
                        {inputFieldError?.image && <div style={{ color: "#D32F2F", fontSize: "12.5px", padding: "10px 0 0 12px", }}>{inputFieldError?.image}</div>}
                    </Grid>


                    <Grid item lg={12} sm={12} md={12} xs={12}>
                        <TextField label="Title" variant="outlined" fullWidth
                            name="title"
                            value={inputFieldDetail?.title}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.title ? true : false}
                            helperText={inputFieldError.title}
                            onFocus={() => handleInputFieldDetailError("title", null)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <FormControl size="small" style={{ minWidth: '180px' }}>
                                <InputLabel>Font</InputLabel>
                                <Select label="Font" defaultValue="" onChange={(e) => { const opt = FONT_OPTIONS.find(o => o.label === e.target.value); applyGroupStyle(FONT_OPTIONS, opt?.style); }}>
                                    {FONT_OPTIONS.map(o => <MenuItem key={o.label} value={o.label} style={o.css}>{o.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small" style={{ minWidth: '150px' }}>
                                <InputLabel>Text Color</InputLabel>
                                <Select label="Text Color" defaultValue="" onChange={(e) => { const opt = COLOR_OPTIONS.find(o => o.label === e.target.value); applyGroupStyle(COLOR_OPTIONS, opt?.style); }}>
                                    {COLOR_OPTIONS.map(o => <MenuItem key={o.label} value={o.label} style={o.css}>{o.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </div>
                        <div style={{ fontSize: '12px', color: 'gray', marginBottom: '8px' }}>Select text in the editor below, then choose a font or color to apply it. Bold, Italic, Underline and other formatting are in the editor's own toolbar.</div>
                        <RichTextEditor
                            value={inputFieldDetail?.content}
                            onChange={value => handleInputFieldDetail({ target: { name: 'content', value: value } })}
                            editorStyle={{ minHeight: '50vh', }}
                            customStyleMap={blogCustomStyleMap}
                            onFocus={() => handleInputFieldDetailError("content", null)}
                        />
                        {inputFieldError?.content && <div style={{ color: "#D32F2F", fontSize: "13px", padding: "5px 15px 0 12px", fontWeight: "400" }}>{inputFieldError?.content}</div>}
                    </Grid>

                    <Grid item lg={6} sm={12} md={12} xs={12}>
                        <TextField label="Tags" variant="outlined" fullWidth
                            name="tags"
                            value={inputFieldDetail?.tags}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.tags ? true : false}
                            helperText={inputFieldError.tags}
                            onFocus={() => handleInputFieldDetailError("tags", null)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item lg={6} sm={12} md={12} xs={12}>
                        <TextField label="Author" variant="outlined" fullWidth
                            name="author"
                            value={inputFieldDetail?.author}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.author ? true : false}
                            helperText={inputFieldError.author}
                            onFocus={() => handleInputFieldDetailError("author", null)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12} sx={{ color: "#000" }}>
                        <div style={{ display: "flex", gap: "40px", flexWrap: "wrap", justifyContent: "space-evenly", marginBottom: "20px", padding: bulkImage?.length > 0 ? '30px 10px' : '', border: bulkImage?.length > 0 ? `1px solid ${Color?.primary}` : '', borderRadius: '4px' }}>
                            {bulkImage?.length > 0 && bulkImage?.map((value, index) => (
                                <div key={index} style={{ position: "relative" }}>
                                    <div style={{ height: '150px' }}>
                                        <Avatar src={value.file} style={{ height: '100%', width: '100%', borderRadius: "initial", objectFit: 'contain' }} />
                                    </div>
                                    <div onClick={() => setBulkImage(bulkImage?.filter((curr, currIndex) => currIndex !== index))} style={{ position: "absolute", top: '-13px', right: '-15px', cursor: "pointer", color: 'red' }}><CrossSvg /></div>
                                </div>
                            ))}
                        </div>

                        <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "13px", color: "gray" }}>Upload More Images(Max File Count : 5)</div>
                        <label onDragOver={(e) => e.preventDefault()} htmlFor="upload-bulk-image" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "25px", cursor: "pointer", border: "1px solid #C4C4C4", borderRadius: "3.5px", padding: "5px 0", backgroundColor: "#F1F1F7" }}>
                            <UploadImageSvg h="25" w="25" color="#000" />
                            <div style={{ fontWeight: "600", fontSize: "15px" }}>Upload</div>
                        </label>
                        <input id="upload-bulk-image" multiple type="file" onChange={handleBulkImage} hidden />
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid item>
                                <div onClick={() => navigate(-1)} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', backgroundColor: Color.white, color: Color.primary, fontSize: '14px', cursor: 'pointer' }}>Cancel</div>
                            </Grid>

                            <Grid item>
                                <div onClick={handleSubmit} style={{  padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color.white, fontSize: '14px', cursor: 'pointer' }}>{mode} Blog</div>
                            </Grid>
                        </Grid>
                    </Grid>

                </Grid>
            </div>
        </>
    )
}

export default AddBlogs;